import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Spinner,
  Table
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axiosInstance from 'axiosInstance';
import PageHeader from 'components/common/PageHeader';

const ACCESS_LEVEL_VARIANTS = {
  view_only: 'info',
  advisor: 'primary',
  manager: 'success',
  none: 'secondary'
};

const PartnerAccessManagementPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [accesses, setAccesses] = useState([]);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // access_id being actioned

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axiosInstance.get('login/partner-access');
      setAccesses(resp.data.accesses || []);
    } catch (e) {
      setError(e.response?.data?.error || t('PARTNERS.MANAGE.ERROR_LOAD'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAction = async (accessId, action) => {
    setActionLoading(accessId);
    setActionError('');
    try {
      await axiosInstance.put(`login/partner-access/${accessId}`, { action });
      load();
    } catch (e) {
      setActionError(e.response?.data?.error || t('PARTNERS.MANAGE.ACTION_ERROR'));
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-6">
        <Spinner animation="border" />
      </div>
    );
  }

  const pending = accesses.filter(a => !a.is_active && !a.revoked_at);
  const active = accesses.filter(a => a.is_active);
  const revoked = accesses.filter(a => !a.is_active && a.revoked_at);

  return (
    <>
      <PageHeader
        title={t('PARTNERS.MANAGE.TITLE')}
        description={t('PARTNERS.MANAGE.DESCRIPTION')}
        className="mb-4"
      />

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {actionError ? <Alert variant="danger" dismissible onClose={() => setActionError('')}>{actionError}</Alert> : null}

      {accesses.length === 0 ? (
        <Card className="border">
          <Card.Body className="text-center text-600 py-5">
            <FontAwesomeIcon icon="handshake" className="fs-3 mb-3 d-block mx-auto text-400" />
            {t('PARTNERS.MANAGE.EMPTY')}
          </Card.Body>
        </Card>
      ) : (
        <>
          {pending.length > 0 && (
            <Card className="border mb-4">
              <Card.Header className="bg-soft-warning">
                <h6 className="mb-0 text-warning">
                  <FontAwesomeIcon icon="clock" className="me-2" />
                  {t('PARTNERS.MANAGE.PENDING_REQUESTS')} ({pending.length})
                </h6>
              </Card.Header>
              <Card.Body className="p-0">
                <AccessTable
                  accesses={pending}
                  showActions
                  actionLoading={actionLoading}
                  onApprove={id => handleAction(id, 'approve')}
                  onRevoke={id => handleAction(id, 'revoke')}
                  t={t}
                />
              </Card.Body>
            </Card>
          )}

          {active.length > 0 && (
            <Card className="border mb-4">
              <Card.Header>
                <h6 className="mb-0">
                  <FontAwesomeIcon icon="check-circle" className="me-2 text-success" />
                  {t('PARTNERS.MANAGE.ACTIVE_ACCESS')} ({active.length})
                </h6>
              </Card.Header>
              <Card.Body className="p-0">
                <AccessTable
                  accesses={active}
                  showActions
                  actionLoading={actionLoading}
                  onRevoke={id => handleAction(id, 'revoke')}
                  t={t}
                />
              </Card.Body>
            </Card>
          )}

          {revoked.length > 0 && (
            <Card className="border">
              <Card.Header>
                <h6 className="mb-0 text-600">
                  {t('PARTNERS.MANAGE.REVOKED')} ({revoked.length})
                </h6>
              </Card.Header>
              <Card.Body className="p-0">
                <AccessTable accesses={revoked} t={t} />
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </>
  );
};

const AccessTable = ({ accesses, showActions, actionLoading, onApprove, onRevoke, t }) => (
  <Table responsive className="mb-0 fs--1">
    <thead className="bg-200 text-900">
      <tr>
        <th>{t('PARTNERS.MANAGE.COL_PARTNER')}</th>
        <th>{t('PARTNERS.MANAGE.COL_EMAIL')}</th>
        <th>{t('PARTNERS.MANAGE.COL_LEVEL')}</th>
        <th>{t('PARTNERS.MANAGE.COL_REQUESTED')}</th>
        <th>{t('PARTNERS.MANAGE.COL_NOTE')}</th>
        {showActions && <th className="text-end">{t('PARTNERS.MANAGE.COL_ACTIONS')}</th>}
      </tr>
    </thead>
    <tbody>
      {accesses.map(access => (
        <tr key={access.id}>
          <td className="align-middle fw-semi-bold">{access.partner.name}</td>
          <td className="align-middle text-600">{access.partner.email}</td>
          <td className="align-middle">
            <Badge bg={ACCESS_LEVEL_VARIANTS[access.level] || 'secondary'}>
              {t(`PARTNERS.ACCESS.${(access.level || 'none').toUpperCase()}`)}
            </Badge>
          </td>
          <td className="align-middle text-600">
            {access.requested_at ? new Date(access.requested_at).toLocaleDateString() : '-'}
          </td>
          <td className="align-middle text-600 text-truncate" style={{ maxWidth: 200 }}>
            {access.note || '-'}
          </td>
          {showActions && (
            <td className="align-middle text-end">
              {!access.is_active && onApprove && (
                <Button
                  size="sm"
                  variant="success"
                  className="me-2"
                  disabled={actionLoading === access.id}
                  onClick={() => onApprove(access.id)}
                >
                  {actionLoading === access.id
                    ? <Spinner size="sm" animation="border" />
                    : t('PARTNERS.MANAGE.APPROVE')}
                </Button>
              )}
              {access.is_active && onRevoke && (
                <Button
                  size="sm"
                  variant="outline-danger"
                  disabled={actionLoading === access.id}
                  onClick={() => onRevoke(access.id)}
                >
                  {actionLoading === access.id
                    ? <Spinner size="sm" animation="border" />
                    : t('PARTNERS.MANAGE.REVOKE')}
                </Button>
              )}
              {!access.is_active && !onApprove && onRevoke && (
                <Button
                  size="sm"
                  variant="outline-danger"
                  disabled={actionLoading === access.id}
                  onClick={() => onRevoke(access.id)}
                >
                  {actionLoading === access.id
                    ? <Spinner size="sm" animation="border" />
                    : t('PARTNERS.MANAGE.REVOKE')}
                </Button>
              )}
            </td>
          )}
        </tr>
      ))}
    </tbody>
  </Table>
);

export default PartnerAccessManagementPage;
